import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, MapPin, Users, Video, Sparkles, FileText, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

interface CalendarEvent {
  id: string;
  provider: 'microsoft' | 'google';
  title: string;
  start: string;
  end: string;
  location: string;
  organizer: string;
  attendees: { name: string; email: string }[];
  is_online: boolean;
  meeting_url: string | null;
  description: string;
  account: string;
}

interface Props {
  event: CalendarEvent;
  onClose: () => void;
}

export function EventDetailPanel({ event, onClose }: Props) {
  const [meetingBrief, setMeetingBrief] = useState<string | null>(null);
  const [meetingNotes, setMeetingNotes] = useState<string | null>(null);

  const generateBrief = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('calendar-ai', {
        body: { action: 'meeting_brief', event },
      });
      if (error) throw error;
      return data.result as string;
    },
    onSuccess: (result) => setMeetingBrief(result),
  });

  const generateNotes = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('meeting-notes-ai', {
        body: { action: 'generate', event },
      });
      if (error) throw error;
      return data.result as string;
    },
    onSuccess: (result) => setMeetingNotes(result),
  });

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">{event.title}</h3>
          <Badge variant="outline" className="mt-1">
            {event.provider === 'microsoft' ? 'Outlook' : 'Google'}
          </Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Separator />

      {/* Time */}
      <div className="text-sm">
        <div className="font-medium">
          {new Date(event.start).toLocaleDateString([], {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          })}
        </div>
        <div className="text-muted-foreground">
          {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {' - '}
          {new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>

      {/* Location */}
      {event.location && (
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {event.location}
        </div>
      )}

      {/* Online meeting */}
      {event.is_online && event.meeting_url && (
        <a
          href={event.meeting_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <Video className="h-4 w-4" />
          Join meeting
        </a>
      )}

      {/* Attendees */}
      {event.attendees.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-sm font-medium mb-1">
            <Users className="h-4 w-4" />
            Attendees ({event.attendees.length})
          </div>
          <div className="space-y-1">
            {event.attendees.map((a, i) => (
              <div key={i} className="text-sm text-muted-foreground">
                {a.name ?? a.email}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description */}
      {event.description && (
        <div>
          <div className="text-sm font-medium mb-1">Description</div>
          <p className="text-sm text-muted-foreground">{event.description}</p>
        </div>
      )}

      <Separator />

      {/* AI Actions */}
      <div className="space-y-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => generateBrief.mutate()}
          disabled={generateBrief.isPending}
        >
          {generateBrief.isPending
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <Sparkles className="mr-2 h-4 w-4" />}
          AI Meeting Brief
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => generateNotes.mutate()}
          disabled={generateNotes.isPending}
        >
          {generateNotes.isPending
            ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            : <FileText className="mr-2 h-4 w-4" />}
          Meeting Notes
        </Button>
      </div>

      {meetingBrief && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Meeting Brief
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{meetingBrief}</p>
          </CardContent>
        </Card>
      )}

      {meetingNotes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" /> Meeting Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{meetingNotes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
